import type { ReactElement, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import getFloatingWrapperPosition from "app/pages/sales-intelligence/common-components/FloatContainer/utils";

import { StyledChildContainer, StyledEmptyContainer, StyledZeroBlock } from "./styles";

interface IProps {
    isFloat: boolean;
    isPositionFixed?: boolean;
    deps?: unknown;
    children: ReactNode;
    className?: string;
}

export const FloatContainer = ({ className, isFloat, isPositionFixed, deps, children }: IProps): ReactElement => {
    const childContainerRef = useRef<HTMLDivElement>(null);
    const zeroBlockRef = useRef<HTMLDivElement>(null);
    const emptyContainerRef = useRef<HTMLDivElement>(null);
    const cbRef = useRef<(() => void) | null>(null);

    const [wrapperTop, setWrapperTop] = useState<number | null>(null);
    const [wrapperLeft, setWrapperLeft] = useState<number | null>(null);

    const { width, height, top = 0, left = 0 } = childContainerRef?.current?.getBoundingClientRect() || {};
    const { top: zeroTop = 0, left: zeroLeft = 0 } = zeroBlockRef?.current?.getBoundingClientRect() || {};

    useEffect(() => {
        if (cbRef.current) {
            document.removeEventListener("scroll", cbRef.current, true);

            cbRef.current = null;
        }

        if (isFloat) {
            cbRef.current = () => {
                if (!childContainerRef.current || !emptyContainerRef.current || !zeroBlockRef.current) {
                    return;
                }

                const emptyContainerRect = emptyContainerRef.current.getBoundingClientRect();
                const zeroBlockRect = zeroBlockRef.current.getBoundingClientRect();

                const position = getFloatingWrapperPosition({
                    isPositionFixed,
                    top: emptyContainerRect.top,
                    left: emptyContainerRect.left,
                    zeroTop: zeroBlockRect.top,
                    zeroLeft: zeroBlockRect.left,
                });

                setWrapperTop(position.top);
                setWrapperLeft(position.left);
            };

            cbRef.current();

            document.addEventListener("scroll", cbRef.current, true);
        } else {
            setWrapperTop(null);
            setWrapperLeft(null);
        }

        return () => {
            if (!cbRef.current) {
                return;
            }
            document.removeEventListener("scroll", cbRef.current, true);
        };
    }, [isFloat, isPositionFixed, deps]);

    const position = getFloatingWrapperPosition({
        isPositionFixed,
        top,
        left,
        zeroTop,
        zeroLeft,
    });

    return (
        <div
            ref={childContainerRef}
            className={className}
        >
            <StyledZeroBlock ref={zeroBlockRef} />

            <StyledChildContainer
                isFloat={isFloat}
                position={isPositionFixed ? "fixed" : "absolute"}
                top={wrapperTop === null ? position.top : wrapperTop}
                left={wrapperLeft === null ? position.left : wrapperLeft}
                key="main"
            >
                {children}
            </StyledChildContainer>

            <StyledEmptyContainer
                ref={emptyContainerRef}
                isFloat={isFloat}
                width={width}
                height={height}
            />
        </div>
    );
};
